import { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import GroupAddRounded from "@mui/icons-material/GroupAddRounded";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

/**
 * ProjectMembersManager – unified members list + add interface (no dialog)
 * - Multi-select from employees with checkboxes
 * - Hide options that are already in members
 * - Unlimited selections (no limitTags)
 * - Optional custom add (name + email)
 * - Inline list with edit/remove (reuses your previous editing dialog)
 */

export default function ProjectMembersManager({
  employees = [],
  members = [],
  setMembers,
  canEdit = true,
  internalStaffCost,
  setInternalStaffCost,
  isAdmin,
  projectManagerEmail,
}) {
  const memberNameRef = useRef(null);
  const [inputValue, setInputValue] = useState("");
  const [draft, setDraft] = useState({ name: "", email: "" });
  const [draftError, setDraftError] = useState("");
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedValues, setEditedValues] = useState({
    name: "",
    email: "",
    role: "",
  });

  const filter = createFilterOptions({
    matchFrom: "any", // match anywhere in the string
    stringify: (opt) => `${opt.name} ${opt.email}`, // fields to search
  });

  useEffect(() => {
    setInternalStaffCost(
      members?.reduce((acc, row, i) => {
        const h = parseFloat(row.hours);
        const r = parseFloat(row.hour_rate);
        const vh = isNaN(h) ? 0 : h;
        const vr = isNaN(r) ? 0 : r;
        return acc + vh * vr;
      }, 0) ?? 0
    );
  }, [members, setInternalStaffCost]);

  const memberEmailSet = useMemo(
    () => new Set(members.map((m) => String(m.email || "").toLowerCase())),
    [members]
  );

  const employeesEmailSet = useMemo(
    () =>
      new Set(
        (Array.isArray(employees) ? employees : []).map((e) =>
          String(e.email || "").toLowerCase()
        )
      ),
    [employees]
  );

  const employeeOptions = useMemo(() => {
    return (Array.isArray(employees) ? employees : [])
      .filter((e) => e && (e.name || e.email))
      .filter((e) => !memberEmailSet.has(String(e.email || "").toLowerCase()));
  }, [employees, memberEmailSet]);

  const isOptionEqualToValue = (opt, val) =>
    (opt?.email || opt?.name) === (val?.email || val?.name);

  const addSelected = (member) => {
    if (!canEdit) return;
    const next = [...members];
    const key = String(member.email || "").toLowerCase();
    if (!memberEmailSet.has(key)) {
      next.push({
        ...member,
        hour_rate: Number.parseInt(
          member.hour_rate?.match(/\d+(?:.\d+)?/)?.[0] ?? 0
        ),
        hours: 0,
        is_custom: false,
      });
    }
    setMembers(next);
  };

  const addDraft = () => {
    if (!canEdit) return;
    const name = draft.name.trim();
    const email = draft.email.trim();
    if (!name || !email) return;
    const key = email.toLowerCase();
    if (memberEmailSet.has(key)) {
      setDraftError("A project member already has this email.");
      return;
    }
    setMembers([
      ...members,
      { name, email, hour_rate: 0, hours: 0, is_custom: true },
    ]);
    setDraft({ name: "", email: "" });
    setDraftError("");
    setTimeout(() => {
      memberNameRef.current?.focus();
    }, 0);
  };

  const handleRemoveMember = (index) => {
    if (!canEdit) return;
    setMembers(members.filter((_, i) => i !== index));
  };

  const handleOpenEdit = (member, index) => {
    setEditingIndex(index);
    setEditedValues({ ...member });
    setOpenEditDialog(true);
  };

  const handleSaveEdit = () => {
    setMembers((prev) =>
      prev.map((m, i) => (i === editingIndex ? editedValues : m))
    );
    setOpenEditDialog(false);
  };

  const handleHoursChange = (index, value, field) => {
    if (value === "") {
      // allow clearing
      setMembers((prev) => {
        const copy = [...prev];
        copy[index] = { ...copy[index], [field]: 0 };
        return copy;
      });
      return;
    }
    const parsed = Number.parseInt(value);

    setMembers((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: parsed };
      return copy;
    });
  };

  useEffect(() => {
    if (employeesEmailSet.has(projectManagerEmail)) {
      setMembers((prev) => {
        const copy = [...prev];
        const next = copy.filter((m) => m.isManager !== true || m.hours !== 0);
        if (memberEmailSet.has(projectManagerEmail)) return next;
        const projectManager = employees.find(
          (e) => e.email === projectManagerEmail
        );
        next.push({
          ...projectManager,
          hour_rate: Number.parseInt(
            projectManager.hour_rate?.match(/\d+(?:.\d+)?/)?.[0] ?? 0
          ),
          hours: 0,
          is_custom: false,
          isManager: true,
        });
        return next;
      });
    }
  }, [projectManagerEmail]);

  return (
    <Box
      sx={{
        position: "relative",
        border: "1px solid rgba(0, 0, 0, 0.23)", // matches TextField default
        borderRadius: 1,
        padding: 2,
        marginTop: 4,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          position: "absolute",
          top: 0,
          left: 8,
          transform: "translateY(-50%)",
          backgroundColor: "white",
          paddingX: 0.5,
          color: "rgba(0, 0, 0, 0.6)", // MUI label default
          fontSize: "0.75rem",
          lineHeight: 1,
        }}
      >
        Project Members
      </Typography>

      <Stack spacing={2}>
        {/* Add members block */}
        <Stack direction="column" gap={1}>
          <Box>
            <Autocomplete
              size="small"
              disableCloseOnSelect
              options={employeeOptions}
              value={null}
              inputValue={inputValue}
              onInputChange={(_, v, reason) => {
                // clear immediately after selection to avoid showing the label
                if (reason === "reset") return setInputValue("");
                setInputValue(v);
              }}
              onChange={(e, val) => addSelected(val)}
              getOptionLabel={(o) => o?.name || o?.email || ""}
              isOptionEqualToValue={isOptionEqualToValue}
              //filterSelectedOptions
              filterOptions={filter}
              sx={{ width: "100%", minWidth: 0 }} // fills the left column
              renderOption={(props, option) => (
                <li {...props} key={option.email || option.name}>
                  <Stack spacing={0}>
                    <Typography variant="body2">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.email}
                    </Typography>
                  </Stack>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search EBS employees"
                  placeholder="Type a name or email"
                />
              )}
            />
          </Box>

          <Stack direction={"row"} gap={1}>
            <TextField
              size="small"
              label="Custom name"
              fullWidth
              value={draft.name}
              onChange={(e) =>
                setDraft((d) => ({ ...d, name: e.target.value }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") addDraft();
              }}
              disabled={!canEdit}
              inputRef={memberNameRef}
              sx={{ width: "40%" }}
            />
            <TextField
              size="small"
              label="Custom email"
              type="email"
              fullWidth
              value={draft.email}
              onChange={(e) => {
                setDraft((d) => ({ ...d, email: e.target.value }));
                if (draftError) setDraftError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") addDraft();
              }}
              error={Boolean(draftError)}
              disabled={!canEdit}
              InputProps={{
                endAdornment: draftError ? (
                  <InputAdornment position="end">
                    <Tooltip title={draftError}>
                      <WarningAmberIcon color="warning" />
                    </Tooltip>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ width: "40%" }}
            />

            <Button
              variant="outlined"
              size="medium"
              startIcon={<GroupAddRounded />}
              onClick={addDraft}
              disabled={!canEdit || draft.email === "" || draft.name === ""}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 9999,
                px: 2.25,
                py: 0.75,
                minHeight: 40,
                minWidth: { xs: "100%", sm: "auto" }, // same as above
                width: { xs: "100%", sm: "25%" },
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                borderColor: "rgb(209, 173, 115)",
                color: "#041e41dc",
                "&:hover": {
                  borderColor: "rgb(209, 173, 115)",
                  backgroundColor: "rgba(209, 173, 115, 0.12)",
                },
              }}
            >
              Add custom
            </Button>
          </Stack>
        </Stack>
        <Divider />
        {/* Members list */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 1 }}
        >
          <Typography variant="body2" color="text.secondary">
            {members.length} {members.length === 1 ? "member" : "members"}
          </Typography>
        </Stack>
        {members.length !== 0 && (
          <List dense sx={{ py: 0 }}>
            {members.map((member, index) => {
              return (
                <ListItem
                  key={index}
                  secondaryAction={
                    canEdit && (
                      <Stack direction="row" spacing={0.5} justifyContent="end">
                        <TextField
                          disabled={!isAdmin && !member.is_custom}
                          placeholder="Hour rate"
                          label={
                            !isAdmin && !member.is_custom ? "" : "Hour rate"
                          }
                          type="number"
                          value={member.hour_rate}
                          onFocus={(e) => {
                            e.target.select();
                          }}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const normalized = raw.replace(/^0+(?=\d)/, "");
                            if (normalized !== raw) {
                              e.target.value = normalized;
                              // keep caret at the end (simple case)
                              try {
                                e.target.setSelectionRange(
                                  normalized.length,
                                  normalized.length
                                );
                              } catch {}
                            }
                            handleHoursChange(index, normalized, "hour_rate");
                          }}
                          onBeforeInput={(e) => {
                            // Blocks non-digits before they hit the input (Chrome/modern)
                            if (e.data && !/^\d+$/.test(e.data))
                              e.preventDefault();
                          }}
                          onPaste={(e) => {
                            const txt = e.clipboardData.getData("text");
                            if (!/^\d+$/.test(txt)) e.preventDefault(); // block non-digit paste
                          }}
                          size="small"
                          fullWidth
                          inputProps={{
                            min: 0,
                            step: 1,
                            maxLength: 16,
                            style: { textAlign: "right" },
                          }}
                          InputProps={{
                            endAdornment: <span>&nbsp;€/h</span>,
                            sx: {
                              "&.Mui-disabled .MuiInputBase-input": {
                                cursor: "text",
                              },
                            },
                          }}
                          sx={{
                            width: "25%",
                            "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                              {
                                WebkitAppearance: "none",
                                margin: 0,
                              },
                            // Firefox
                            "& input[type=number]": {
                              MozAppearance: "textfield",
                            },
                            "& .MuiOutlinedInput-root":
                              isAdmin || member.is_custom
                                ? {}
                                : {
                                    "& fieldset": { border: "none" }, // default
                                    "&:hover fieldset": { border: "none" }, // hover
                                    "&.Mui-focused fieldset": {
                                      border: "none",
                                    }, // focus
                                  },
                          }}
                        />

                        <TextField
                          label="Workload"
                          placeholder="Workload"
                          type="number"
                          value={member.hours}
                          onFocus={(e) => {
                            e.target.select();
                          }}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const normalized = raw.replace(/^0+(?=\d)/, "");
                            if (normalized !== raw) {
                              e.target.value = normalized;
                              // keep caret at the end (simple case)
                              try {
                                e.target.setSelectionRange(
                                  normalized.length,
                                  normalized.length
                                );
                              } catch {}
                            }
                            handleHoursChange(index, normalized, "hours");
                          }}
                          onBeforeInput={(e) => {
                            // Blocks non-digits before they hit the input (Chrome/modern)
                            if (e.data && !/^\d+$/.test(e.data))
                              e.preventDefault();
                          }}
                          onPaste={(e) => {
                            const txt = e.clipboardData.getData("text");
                            if (!/^\d+$/.test(txt)) e.preventDefault(); // block non-digit paste
                          }}
                          size="small"
                          fullWidth
                          inputProps={{
                            min: 0,
                            step: 1,
                            maxLength: 16,
                            style: { textAlign: "right" },
                          }}
                          InputProps={{
                            endAdornment: <span>&nbsp;h</span>,
                          }}
                          sx={{
                            width: "20%",
                            "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                              {
                                WebkitAppearance: "none",
                                margin: 0,
                              },
                            // Firefox
                            "& input[type=number]": {
                              MozAppearance: "textfield",
                            },
                          }}
                        />
                        {!(
                          !member.is_custom ||
                          employeesEmailSet.has(
                            String(member.email || "").toLowerCase()
                          )
                        ) && (
                          <Tooltip title="Edit member">
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleOpenEdit(member, index)}
                              tabIndex={-1}
                            >
                              <EditIcon fontSize="small" color="primary" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Remove member">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleRemoveMember(index)}
                            tabIndex={-1}
                          >
                            <DeleteIcon fontSize="small" color="error" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )
                  }
                >
                  <ListItemAvatar>
                    <Avatar sx={{ width: 28, height: 28 }}>
                      <PersonIcon fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primaryTypographyProps={{ variant: "body2" }}
                    secondaryTypographyProps={{
                      variant: "caption",
                      color: "text.secondary",
                    }}
                    primary={`${member.name}${
                      member.role ? ` (${member.role})` : ""
                    }`}
                    secondary={member.email}
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Stack>
      {members.length > 0 && (
        <>
          <Divider sx={{ my: 2, backgroundColor: "rgba(0, 0, 0, 0.23)" }} />
          <Typography
            sx={{
              justifySelf: "end",
              p: 1,
              pr: 8,
            }}
          >
            Total Staff Cost: {internalStaffCost} €
          </Typography>
        </>
      )}

      {/* Edit Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        fullWidth
      >
        <DialogTitle>Edit member</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            name="name"
            fullWidth
            margin="normal"
            value={editedValues.name || ""}
            onChange={(e) =>
              setEditedValues((prev) => ({ ...prev, name: e.target.value }))
            }
          />
          <TextField
            label="Email"
            name="email"
            fullWidth
            margin="normal"
            value={editedValues.email || ""}
            onChange={(e) =>
              setEditedValues((prev) => ({ ...prev, email: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
